'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Input } from '@/components/Input';
import { Label } from '@/components/Label';
import { Button } from '@/components/ui/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';

import { login } from '@/services/bff/auth.repository';

export default function LoginForm() {
  const router = useRouter();
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data: any) => {
    setGlobalError(null);
    try {
      // El flag `remember` lo procesa el servidor para extender la cookie (30d vs 1d)
      await login({ ...data, remember: rememberMe });
      setIsNavigating(true);
      router.push('/admin');
      router.refresh();
    } catch (error: any) {
      setGlobalError(error.message || 'Credenciales inválidas');
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit(onSubmit)(e);
      }}
      className="mt-8 space-y-6 text-left"
    >
      <div className="bg-white/70 backdrop-blur p-5 space-y-4">
        {/* Email */}
        <div>
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="Correo electrónico"
            {...register('email', { required: 'El email es requerido' })}
          />
          {errors.email && (
            <p className="text-sm text-red-500 mt-1">{errors.email.message}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <Label htmlFor="password">Contraseña</Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Contraseña"
              {...register('password', { required: 'La contraseña es requerida' })}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 h-full px-3 text-gray-500 hover:text-gray-700 hover:bg-transparent"
            >
              <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
            </Button>
          </div>
          {errors.password && (
            <p className="text-sm text-red-500 mt-1">{errors.password.message}</p>
          )}
        </div>

        {/* Remember me + Forgot password */}
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 accent-primary"
            />
            <span className="text-sm text-gray-600">Recordarme</span>
          </label>
          <a href="/olvide-pass" className="text-sm text-primary-500 hover:underline">
            ¿Olvidaste tu contraseña?
          </a>
        </div>

        {/* Global Error */}
        {globalError && (
          <p className="text-sm font-medium text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
            {globalError}
          </p>
        )}

        <Button
          type="submit"
          disabled={isSubmitting || isNavigating}
          className="w-full"
        >
          {isSubmitting || isNavigating ? 'Ingresando...' : 'Ingresar'}
        </Button>
      </div>
    </form>
  );
}
